import { makeStyles } from "@fluentui/react-components";
import { KONTAKTDATEN_FORMATS, subPath } from "@kmvs/forms-core";
import EntryField from "./EntryField";
import type { Kontaktdaten } from "./types";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  adresseCell: {
    "@media (min-width: 640px)": {
      gridColumn: "1 / span 2",
    },
  },
});

type Props = {
  value: Kontaktdaten | undefined;
  onChange: (next: Kontaktdaten) => void;
  /** Flacher Answer-Key der Liste, in der dieser Kontakt hängt. */
  listKey: string;
  /**
   * Pfad des Kontakt-Objekts, z. B. `gesellschafter[#3f9a…].kontakt`.
   * Verpflichtend, damit keine Instanz dieser Gruppe ungeprüft rendert und
   * jede Meldung an ihrem eigenen Eintrag hängt.
   */
  pfad: string;
};

// Sauberer Split: Telefon, Mobil, E-Mail — jeweils privat / geschäftlich.
// Keine Mischbegriffe wie "Telefon mobil (privat)". Welches Feld welche
// Prüfregel bekommt, steht in forms-core (KONTAKTDATEN_FORMATS); hier steht
// nur, wie es heißt.
const FIELDS: Array<{ key: keyof typeof KONTAKTDATEN_FORMATS; label: string }> = [
  { key: "telefonPrivat", label: "Telefon (privat)" },
  { key: "telefonGeschaeftlich", label: "Telefon (geschäftlich)" },
  { key: "mobilPrivat", label: "Mobil (privat)" },
  { key: "mobilGeschaeftlich", label: "Mobil (geschäftlich)" },
  { key: "emailPrivat", label: "E-Mail (privat)" },
  { key: "emailGeschaeftlich", label: "E-Mail (geschäftlich)" },
];

const KontaktdatenGroup = ({ value, onChange, listKey, pfad }: Props) => {
  const styles = useStyles();
  const v = value ?? {};
  const set = (key: keyof Kontaktdaten, next: string) =>
    onChange({ ...v, [key]: next });

  return (
    <div className={styles.grid}>
      {FIELDS.map((f) => (
        <EntryField
          key={f.key}
          label={f.label}
          listKey={listKey}
          pfad={subPath(pfad, f.key)}
          format={KONTAKTDATEN_FORMATS[f.key]}
          value={v[f.key] ?? ""}
          onChange={(next) => set(f.key, next)}
        />
      ))}
      {/* Die Adresse bleibt bewusst ohne Format: "Straße, PLZ, Ort" in einem
          Feld besteht keine Einzelwertprüfung. */}
      <EntryField
        label="Adresse"
        listKey={listKey}
        pfad={subPath(pfad, "adresse")}
        value={v.adresse ?? ""}
        placeholder="Straße, PLZ, Ort"
        onChange={(next) => set("adresse", next)}
        className={styles.adresseCell}
      />
    </div>
  );
};

export default KontaktdatenGroup;
